/**
 * Super Admin - Create User
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useToast } from '../../components/Toast/useToast';
import { getFunctions, httpsCallable } from 'firebase/functions';

export function CreateUser() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'company_user',
    companyId: '',
    active: true
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'companies'));
      const companiesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCompanies(companiesList);
      
      // Si solo hay una empresa (CEJM), seleccionarla por defecto
      if (companiesList.length === 1) {
        setFormData(prev => ({ ...prev, companyId: companiesList[0].id }));
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.email || !formData.password || !formData.name) {
      showToast({ type: 'error', message: 'Completa todos los campos requeridos' });
      return;
    }

    if (formData.password.length < 6) {
      showToast({ type: 'error', message: 'La contraseña debe tener al menos 6 caracteres' });
      return;
    }

    if (formData.role !== 'super_admin' && !formData.companyId) {
      showToast({ type: 'error', message: 'Selecciona una empresa' });
      return;
    }

    setLoading(true);

    try {
      const functions = getFunctions();
      const createCompanyUser = httpsCallable(functions, 'createCompanyUser');
      
      const userData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: formData.role,
        companyId: formData.role === 'super_admin' ? null : formData.companyId,
        active: formData.active
      };

      const result = await createCompanyUser(userData);
      
      if (result.data.success) {
        showToast({ type: 'success', message: '✅ Usuario creado exitosamente' });
        navigate('/super-admin/users');
      } else {
        throw new Error(result.data.error || 'Error al crear usuario');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      showToast({ 
        type: 'error', 
        message: error.message || 'Error al crear usuario' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid" style={{ gap: '24px', maxWidth: '600px' }}>
      {/* Header */}
      <div>
        <h1 className="title" style={{ fontSize: '28px', marginBottom: '8px' }}>Crear Usuario</h1>
        <p className="meta">Añadir un nuevo usuario al sistema</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card">
        <div className="grid" style={{ gap: '16px' }}>
          {/* Email */}
          <div>
            <label className="kicker" style={{ display: 'block', marginBottom: '8px' }}>
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="usuario@ejemplo.com"
              required
              disabled={loading}
            />
          </div>

          {/* Password */}
          <div>
            <label className="kicker" style={{ display: 'block', marginBottom: '8px' }}>
              Contraseña * (mínimo 6 caracteres)
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="••••••••"
              minLength={6}
              required
              disabled={loading}
            />
          </div>

          {/* Name */}
          <div>
            <label className="kicker" style={{ display: 'block', marginBottom: '8px' }}>
              Nombre completo *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Juan Pérez"
              required
              disabled={loading}
            />
          </div>

          {/* Role */}
          <div>
            <label className="kicker" style={{ display: 'block', marginBottom: '8px' }}>
              Rol *
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
              disabled={loading}
            >
              <option value="company_user">👤 Usuario de Empresa</option>
              <option value="company_admin">🏢 Administrador de Empresa</option>
              <option value="super_admin">👑 Super Admin</option>
            </select>
            <p className="meta" style={{ marginTop: '4px' }}>
              {formData.role === 'super_admin' && '👑 Acceso total al sistema'}
              {formData.role === 'company_admin' && '🏢 Puede gestionar su empresa y usuarios'}
              {formData.role === 'company_user' && '👤 Puede subir y firmar documentos'}
            </p>
          </div>

          {/* Company (only if not super_admin) */}
          {formData.role !== 'super_admin' && (
            <div>
              <label className="kicker" style={{ display: 'block', marginBottom: '8px' }}>
                Empresa *
              </label>
              <select
                value={formData.companyId}
                onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                required
                disabled={loading}
              >
                <option value="">Selecciona una empresa</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Active */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              disabled={loading}
            />
            <label htmlFor="active" className="kicker" style={{ margin: 0, cursor: 'pointer' }}>
              Usuario activo
            </label>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button
              type="submit"
              className="btn"
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? 'Creando...' : '✅ Crear Usuario'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/super-admin/users')}
              className="copy"
              disabled={loading}
            >
              Cancelar
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CreateUser;
